/**
 * Known Bugs list for the Traveller Ship Builder application
 */

/**
 * IMPORTANT NOTE FOR USERS:
 * 
 * The Module Builder and Breakaway Hulls systems are currently broken and will 
 * remain broken until bugs in the Main Ship Builder are fixed first, as these 
 * systems are dependent on working code in the main Ship Builder.
 * 
 * Bugs for the Module Builder and Breakaway Hulls will be added and addressed 
 * once we can confirm that the Main Ship Builder is working to a reasonable standard.
 * 
 * Thank you for your patience and understanding.
 */
export const SYSTEM_STATUS_NOTE = `**IMPORTANT:** The Module Builder and Breakaway Hulls systems are currently broken and will remain broken until bugs in the Main Ship Builder are fixed first, as these systems are dependent on working code in the main Ship Builder. Bugs for those systems will be added and addressed once we can confirm that the Main Ship Builder is working to a reasonable standard.`;

export interface BugEntry {
  id: number;
  title: string;
  status: 'pending' | 'fixed';
  priority: 'high' | 'medium' | 'low';
  description: string;
  problem?: string;
  impact?: string;
  travellerRules?: string[];
  version?: string; // Version fixed in (if fixed)
}

export const KNOWN_BUGS: BugEntry[] = [
  // HIGH PRIORITY BUGS
  {
    id: 21,
    title: 'Missing Hull Size Validation Rules',
    status: 'pending',
    priority: 'high',
    description: 'No validation for minimum hull size requirements per Traveller rules',
    problem: 'Users can create ships smaller than 5 tons or jump-capable ships smaller than 100 tons',
    impact: 'Ships that violate Traveller rules can be created, affecting game balance and realism',
    travellerRules: [
      'A hull must be a minimum of five tons',
      'Jump-capable ships must be at least 100 tons',
      'A hull has no upper size limit'
    ]
  },
  {
    id: 25,
    title: 'Drones Tonnage Calculation Bug',
    status: 'pending',
    priority: 'high',
    description: 'Drones are specified in # of drones, but added as # of Dtons of drones',
    problem: 'If user specifies 10 drones (=2 Dton), they get 20 Dtons of drones instead of 2 Dtons',
    impact: 'Incorrect tonnage calculations for drone systems, affecting available space'
  },
  {
    id: 29,
    title: 'Turret Count Ignored in Summary',
    status: 'pending',
    priority: 'high',
    description: 'Number of turrets ignored, only one of each type presented in summary',
    problem: 'If multiple turrets of same type installed, summary only shows one',
    impact: 'Inaccurate weapon system summary'
  },
  {
    id: 35,
    title: 'Small Craft Rules Implementation',
    status: 'pending',
    priority: 'high',
    description: 'Comprehensive implementation of small craft-specific rules',
    problem: 'Multiple rule violations and missing features for small craft (≤100 tons): weapon restrictions (no bays/spinals), crew requirements (pilot only, no engineering), stateroom requirements (not needed by default), Virtual Crew system',
    impact: 'Multiple game rule violations, incorrect crew/tonnage calculations, missing autonomous ship capability',
    travellerRules: [
      'Ships of 100 tons or less that do not possess a jump drive typically have a single pilot',
      'The journeys undertaken by such ships are generally short and it is assumed that regular maintenance removes the need for mid-voyage engineering',
      'Virtual Crew can replace up to five pilots, gunners or sensor operators on board a ship, potentially allowing the ship to act autonomously'
    ]
  },
  {
    id: 36,
    title: 'Firmpoint System Implementation',
    status: 'pending',
    priority: 'high',
    description: 'Comprehensive implementation of firmpoint weapon mounting rules',
    problem: 'Multiple firmpoint rules not implemented or incorrectly calculated: weapon limits (one per firmpoint), turret upgrades, barbette restriction (3 firmpoints), power calculation (75% + 1), range reduction, gunner requirements',
    impact: 'Multiple game rule violations, incorrect power/range calculations, improper weapon mounting',
    travellerRules: [
      'Every firmpoint can only mount one turret weapon, whether in a Fixed Mount or Single Turret',
      'One (and only one) Firmpoint can be upgraded to a single (not double or triple) turret',
      'Barbettes consume three Firmpoints',
      'Power requirements of the weapon are reduced by 25% (rounding up)',
      'A pilot may fire any weapons in a single turret at DM-2 to the attack roll or weapons noted as being in fixed mounts'
    ]
  },
  {
    id: 37,
    title: 'Med Bay Specifications Incorrect',
    status: 'pending',
    priority: 'high',
    description: 'Med Bay specifications incorrect',
    problem: 'Should be 4 Dton, MCr 2, and 1 Power',
    impact: 'Incorrect cost, tonnage, and power calculations for medical facilities'
  },
  {
    id: 38,
    title: 'Docking Systems and Small Craft Handling Major Overhaul',
    status: 'pending',
    priority: 'high',
    description: 'Comprehensive overhaul needed for docking spaces, hangars, and small craft systems',
    problem: 'Multiple inconsistencies: docking space sizing errors, capacity issues, summary inconsistencies, empty spaces not shown, craft costs not included in totals, launch tube issues',
    impact: 'Major system-wide inconsistency affecting small craft integration, inaccurate costs, confusing UX'
  },
  {
    id: 39,
    title: 'Drop Tanks Broken',
    status: 'pending',
    priority: 'high',
    description: 'Drop tanks taking up internal storage space and not adding fuel quantity',
    problem: 'Drop tanks should be external and should add to available fuel, but currently taking internal space and not adding fuel to calculations',
    impact: 'Drop tanks completely non-functional'
  },

  // MEDIUM PRIORITY BUGS
  {
    id: 13,
    title: 'Crew Calculations Issues',
    status: 'pending',
    priority: 'medium',
    description: 'Crew calculations may be incorrect or incomplete',
    problem: 'Need to verify crew requirement calculations are accurate',
    impact: 'Incorrect crew requirements affecting ship operations'
  },
  {
    id: 18,
    title: 'Jump Control Bandwidth Not Considering Bis',
    status: 'pending',
    priority: 'medium',
    description: 'Bis (bandwidth) not considered for Jump Control bandwidth calculations',
    problem: 'Missing bandwidth calculation for jump control systems',
    impact: 'Incorrect bandwidth requirements for jump control'
  },
  {
    id: 26,
    title: 'Summary Table Not Implemented',
    status: 'pending',
    priority: 'medium',
    description: 'Need to implement a true "Summary Table" feature',
    problem: 'Current summary display doesn\'t provide comprehensive overview',
    impact: 'Users lack clear overview of complete ship configuration'
  },
  {
    id: 27,
    title: 'UI Boxes Not Clickable',
    status: 'pending',
    priority: 'medium',
    description: '"Boxes" should be clickable buttons, not just containers for radio selects',
    problem: 'Poor UX - users expect to click the entire box, not just the radio button',
    impact: 'Confusing user interaction patterns'
  },
  {
    id: 28,
    title: 'Medic Not Included in Crew Summary',
    status: 'pending',
    priority: 'medium',
    description: 'Medic position may not be displayed in crew summary',
    problem: 'Medical staff requirements not visible in crew overview',
    impact: 'Incomplete crew planning and display'
  },
  {
    id: 30,
    title: 'Missile Turrets Incorrectly Using Combined Damage',
    status: 'pending',
    priority: 'medium',
    description: 'Missile turrets shouldn\'t have combined damage display',
    problem: 'When multiple missile turrets installed, damage incorrectly aggregated',
    impact: 'Unclear total missile damage capability'
  },
  {
    id: 34,
    title: 'Detachable Bridge Cost Verification',
    status: 'pending',
    priority: 'medium',
    description: 'Need to verify cost calculation for detachable bridge',
    problem: 'Detachable bridge cost may be incorrect',
    impact: 'Potential incorrect ship cost calculations'
  },
  {
    id: 40,
    title: 'Armoured Bulkheads Drive Size Rounding Error',
    status: 'pending',
    priority: 'medium',
    description: 'Rounding error in drive size when adding armoured bulkheads',
    problem: 'Drive size calculation incorrect when armoured bulkheads installed',
    impact: 'Incorrect tonnage calculations'
  },

  // LOW PRIORITY BUGS
  {
    id: 17,
    title: 'Footer Label Inconsistency',
    status: 'pending',
    priority: 'low',
    description: '"Bridge Power" label should be "Power Required"',
    problem: 'Misleading label in footer display',
    impact: 'Confusion about what the power value represents'
  },
  {
    id: 14,
    title: 'Stateroom Configuration Limitations',
    status: 'pending',
    priority: 'low',
    description: 'Stateroom configuration stuck on "all or nothing" calculation',
    problem: 'Cannot mix single and double occupancy staterooms effectively',
    impact: 'Limited flexibility in crew accommodation planning'
  }
];

/**
 * Get bugs by priority
 */
export const getBugsByPriority = (priority: 'high' | 'medium' | 'low'): BugEntry[] => {
  return KNOWN_BUGS.filter(bug => bug.priority === priority && bug.status === 'pending');
};

/**
 * Get bugs by status
 */
export const getBugsByStatus = (status: 'pending' | 'fixed'): BugEntry[] => {
  return KNOWN_BUGS.filter(bug => bug.status === status);
};

/**
 * Get total bug count
 */
export const getTotalBugCount = (): number => {
  return KNOWN_BUGS.filter(bug => bug.status === 'pending').length;
};

/**
 * Get fixed bug count
 */
export const getFixedBugCount = (): number => {
  return KNOWN_BUGS.filter(bug => bug.status === 'fixed').length;
};

/**
 * Get bug completion percentage
 */
export const getBugCompletionPercentage = (): number => {
  const total = KNOWN_BUGS.length;
  const fixed = getFixedBugCount();
  return Math.round((fixed / total) * 100);
};

